import React, { useState } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { addComment, extractMentions } from '../../lib/comments';
import { CommentWithProfile } from '../../lib/comments';

interface CommentFormProps {
  ideaId?: string;
  taskId?: string;
  onCommentAdded?: (comment: CommentWithProfile) => void;
  onCancel?: () => void;
  placeholder?: string;
  className?: string;
}

const CommentForm: React.FC<CommentFormProps> = ({
  ideaId,
  taskId,
  onCommentAdded,
  onCancel,
  placeholder = "Write a comment...",
  className = ""
}) => {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!content.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Extract mentions from content
      const mentions = extractMentions(content);
      
      // Add the comment
      const newComment = await addComment(content, ideaId, taskId, mentions);
      
      if (newComment) {
        setContent('');
        onCommentAdded?.(newComment);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit(e);
    }
  };

  return (
    <div className={`bg-white border rounded-lg p-4 ${className}`}>
      <form onSubmit={handleSubmit}>
        <div className="space-y-3">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-deep-teal focus:border-transparent resize-none"
            disabled={isSubmitting}
          />
          
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500">
              <span>Press Cmd/Ctrl + Enter to submit</span>
            </div>
            
            <div className="flex items-center space-x-2">
              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              )}
              
              <button
                type="submit"
                disabled={!content.trim() || isSubmitting}
                className="flex items-center px-4 py-1.5 text-sm font-medium text-white bg-deep-teal rounded-lg hover:bg-deep-teal/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Posting...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                    Post Comment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CommentForm; 